import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/UI/card';
import { Button } from '@/components/UI/button';
import { Download, Loader2 } from 'lucide-react';
import { toast } from '@/components/UI/use-toast';

const StatementOfAccount = ({ invoiceService }) => {
  const [soaData, setSOAData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchSOAData();
  }, []);

  const fetchSOAData = async () => {
    try {
      const data = await invoiceService.getClientSOA();
      setSOAData(data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch SOA data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await invoiceService.downloadClientSOA();
      toast({
        title: "Success",
        description: "SOA downloaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to download SOA",
        variant: "destructive"
      });
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Statement of Account</CardTitle>
        <Button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-2"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Download Excel
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left border">INV #</th>
                <th className="p-2 text-left border">Client</th>
                <th className="p-2 text-right border">Sales</th>
                <th className="p-2 text-right border">Retention Deduction</th>
                <th className="p-2 text-right border">P.Retention Deduction</th>
                <th className="p-2 text-right border">Advance Deduction/Release</th>
                <th className="p-2 text-right border">Release of Retention</th>
                <th className="p-2 text-right border">Deduction/Discount</th>
                <th className="p-2 text-right border">Amount W/out Vat</th>
                <th className="p-2 text-right border">VAT Amount</th>
                <th className="p-2 text-right border">Total amount</th>
              </tr>
            </thead>
            <tbody>
              {soaData.rows.map((row, index) => (
                <tr key={row.invoice_no} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="p-2 border">{row.invoice_no}</td>
                  <td className="p-2 border">{row.client_name}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_value)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_retention_total)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_performance_retention_total)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.advance_release_total)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.retention_release_total)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.other_deduction_total)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_value)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_value * 0.05)}</td>
                  <td className="p-2 text-right border">{formatCurrency(row.cumulative_value * 1.05)}</td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="p-2 border" colSpan={2}>Totals</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.sales)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.retentionDeduction)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.pRetentionDeduction)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.advanceDeduction)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.retentionRelease)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.otherDeduction)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.amountWithoutVat)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.vatAmount)}</td>
                <td className="p-2 text-right border">{formatCurrency(soaData.totals.totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatementOfAccount;